import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, ShieldAlert, GanttChartSquare, Boxes, TrendingUp, Landmark, Radar,
  Search, Bell, Settings, Command, ArrowLeft, Send, Sparkles, X, Bot, Menu, Lightbulb, Plug,
  ScanSearch, FileText, TriangleAlert, Filter, Route, GitBranch, Presentation, ChevronRight, BrainCircuit, Users, LogOut,
  Tv, Copy, Check, Loader2, RadioTower, ExternalLink, History, Waypoints, PlayCircle,
} from "lucide-react";
import { NexSysWordmark, NexSysMark, Pill } from "./ui";
import { NAV, COMMANDS } from "./data";
import { useNexsysGate, gateApi } from "./gate";
import { ConflictAlert } from "./IdentityBindings";
import { nexsysApi } from "./api";

const ICONS = {
  LayoutDashboard, ShieldAlert, GanttChartSquare, Boxes, TrendingUp, Landmark, Radar, Lightbulb, Plug,
  ScanSearch, FileText, TriangleAlert, Filter, Route, GitBranch, Presentation, BrainCircuit, Users, History, Waypoints, PlayCircle,
};

/* ==================== SIDEBAR ==================== */
export function Sidebar({ view, setView }) {
  const navigate = useNavigate();
  const { identity, logout } = useNexsysGate();
  const isAdmin = !!identity?.is_admin;
  const initials = (identity?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <aside className="hidden w-[240px] shrink-0 flex-col border-r border-white/[0.06] bg-black/20 px-3 py-5 lg:flex">
      <div className="px-2"><NexSysWordmark showTag /></div>

      <nav className="mt-8 flex-1 space-y-1">
        <div className="px-3 pb-2 font-mono text-[10px] tracking-[0.2em] text-white/25">OPERATIONS</div>
        {NAV.filter((item) => !item.admin || isAdmin).map((item) => {
          const Icon = ICONS[item.icon] || LayoutDashboard;
          const active = view === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              data-testid={`nav-${item.id}`}
              className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                active ? "text-white" : "text-white/50 hover:text-white/90"
              }`}
            >
              {active && (
                <motion.span layoutId="nav-active" className="absolute inset-0 rounded-xl border border-cyan-400/30 bg-cyan-400/10 nx-neon-border" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
              )}
              <Icon className={`relative z-10 h-4 w-4 ${active ? "text-cyan-300" : ""}`} />
              <span className="relative z-10 font-medium">{item.label}</span>
              {active && <span className="relative z-10 ml-auto h-1.5 w-1.5 rounded-full bg-cyan-300 nx-pulse" />}
            </button>
          );
        })}
      </nav>

      <div className="space-y-1 border-t border-white/[0.06] pt-3">
        <button onClick={() => navigate("/")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/50 hover:text-white/90" data-testid="back-to-site">
          <ArrowLeft className="h-4 w-4" /> Back to Nexis.team
        </button>
        <button onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/50 hover:text-red-300" data-testid="gate-signout">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
        <div className="mt-2 flex items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500 text-xs font-bold text-black">{initials}</div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-xs font-medium text-white/90">{identity?.name || "Guest"}{isAdmin && " · Admin"}</div>
            <div className="truncate font-mono text-[9px] text-emerald-400">● {identity?.email || "signed in"}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

/* ==================== MOBILE NAV DRAWER ==================== */
export function MobileNav({ open, setOpen, view, setView }) {
  const navigate = useNavigate();
  const { identity, logout } = useNexsysGate();
  const isAdmin = !!identity?.is_admin;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] lg:hidden" data-testid="mobile-nav"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.aside
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="relative flex h-full w-[280px] max-w-[82vw] flex-col border-r border-white/10 bg-[#070b16]/95 px-3 py-5 backdrop-blur-2xl"
          >
            <div className="flex items-center justify-between px-2">
              <NexSysWordmark showTag />
              <button onClick={() => setOpen(false)} data-testid="mobile-nav-close" className="rounded-lg p-1.5 text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <nav className="nx-scroll mt-8 flex-1 space-y-1 overflow-y-auto">
              <div className="px-3 pb-2 font-mono text-[10px] tracking-[0.2em] text-white/25">OPERATIONS</div>
              {NAV.filter((item) => !item.admin || isAdmin).map((item) => {
                const Icon = ICONS[item.icon] || LayoutDashboard;
                const active = view === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { setView(item.id); setOpen(false); }}
                    data-testid={`mobile-nav-${item.id}`}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors ${
                      active ? "border border-cyan-400/30 bg-cyan-400/10 text-white" : "text-white/55 hover:text-white/90"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${active ? "text-cyan-300" : ""}`} />
                    <span className="font-medium">{item.label}</span>
                    {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-cyan-300 nx-pulse" />}
                  </button>
                );
              })}
            </nav>
            <div className="space-y-1 border-t border-white/[0.06] pt-3">
              <button onClick={() => navigate("/")} data-testid="mobile-back-to-site" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/55 hover:text-white/90">
                <ArrowLeft className="h-4 w-4" /> Back to Nexis.team
              </button>
              <button onClick={logout} data-testid="mobile-gate-signout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/55 hover:text-red-300">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ==================== CAST / PRESENT MODAL ==================== */
export function CastModal({ open, onClose }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const kioskUrl = `${window.location.origin}/nexsys?kiosk=1`;

  useEffect(() => {
    if (!open) return;
    setCode(""); setError(""); setCopied(false); setBusy(true);
    gateApi.pairCreate()
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not create a code");
        setCode(d.code);
      })
      .catch((e) => setError(e.message || "Could not create a code"))
      .finally(() => setBusy(false));
  }, [open]);

  const copy = (text) => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1600); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
          onClick={onClose} data-testid="cast-modal"
        >
          <motion.div
            initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="nx-panel w-full max-w-md overflow-hidden p-7 text-center"
          >
            <button onClick={onClose} className="absolute right-4 top-4 text-white/40 hover:text-white" data-testid="cast-close"><X className="h-5 w-5" /></button>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-400/10"><Tv className="h-7 w-7 text-cyan-300" /></div>
            <div className="mt-4 font-display text-xl font-bold text-white">Cast to TV / Present</div>
            <p className="mx-auto mt-2 max-w-xs text-[13px] leading-relaxed text-white/50">
              On your TV browser (Roku etc.), open the presenter link below and enter this one-time code.
            </p>

            {busy ? (
              <div className="my-8 flex items-center justify-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin text-cyan-300" /> Generating code…</div>
            ) : error ? (
              <div className="my-6 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="cast-error">{error}</div>
            ) : (
              <>
                <div className="my-5">
                  <div className="font-mono text-[10px] tracking-[0.2em] text-white/35">PAIRING CODE · VALID 10 MIN</div>
                  <button onClick={() => copy(code)} data-testid="cast-code" className="group mt-2 inline-flex items-center gap-3 rounded-2xl border border-cyan-400/25 bg-cyan-400/5 px-6 py-3 font-mono text-4xl tracking-[0.4em] text-cyan-200 hover:border-cyan-400/50">
                    {code}
                    {copied ? <Check className="h-5 w-5 text-emerald-400" /> : <Copy className="h-5 w-5 text-white/40 group-hover:text-cyan-300" />}
                  </button>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-left">
                  <div className="font-mono text-[10px] tracking-[0.15em] text-white/35">PRESENTER LINK</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="truncate font-mono text-[12px] text-white/70">{kioskUrl}</span>
                    <button onClick={() => copy(kioskUrl)} className="ml-auto shrink-0 rounded-lg border border-white/10 px-2 py-1 text-[11px] text-white/60 hover:text-white" data-testid="cast-copy-url">Copy</button>
                  </div>
                </div>
              </>
            )}

            <a
              href={kioskUrl} target="_blank" rel="noreferrer"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 text-sm font-semibold text-black hover:bg-cyan-400"
              data-testid="cast-open-here"
            >
              <ExternalLink className="h-4 w-4" /> Open presenter here (new tab)
            </a>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ==================== TOP BAR ==================== */
export function TopBar({ view, openPalette, notifCount, onMenu }) {
  const [time, setTime] = useState(new Date());
  const [castOpen, setCastOpen] = useState(false);
  // Header status is the live hosting self-probe, not a hardcoded "nominal".
  const [hostState, setHostState] = useState(null);
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const probe = () => nexsysApi.hosting().then((h) => setHostState(h.status)).catch(() => setHostState("unknown"));
    probe();
    const t = setInterval(probe, 30000);
    return () => clearInterval(t);
  }, []);
  const label = NAV.find((n) => n.id === view)?.label || "Command Center";
  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-white/[0.06] bg-black/20 px-3 sm:gap-4 sm:px-4 lg:px-6">
      <button onClick={onMenu} data-testid="nav-menu-toggle" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/70 hover:text-white lg:hidden">
        <Menu className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2 sm:hidden"><NexSysMark size={24} /></div>
      <div className="hidden items-center gap-2 text-sm lg:flex">
        <span className="font-mono text-[11px] tracking-widest text-cyan-400/70">NEXSYS</span>
        <ChevronRight className="h-3.5 w-3.5 text-white/25" />
        <span className="font-display font-semibold text-white/90">{label}</span>
      </div>

      <button
        onClick={openPalette}
        data-testid="open-command-palette"
        className="group ml-auto flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/40 transition-colors hover:border-cyan-400/30 sm:w-full sm:max-w-sm sm:flex-none"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden truncate sm:inline">Search or run a command…</span>
        <span className="truncate sm:hidden">Search…</span>
        <kbd className="ml-auto hidden items-center gap-1 rounded-md border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-white/40 sm:flex">
          <Command className="h-3 w-3" />K
        </kbd>
      </button>

      <div className="hidden items-center gap-2 xl:flex">
        <Pill tone={hostState === "live" ? "emerald" : hostState === "degraded" ? "danger" : "amber"}>
          <span data-testid="topbar-host-status">{hostState === "live" ? "APP HOSTING · LIVE" : hostState === "degraded" ? "APP HOSTING · FAILED" : hostState ? "HOSTING STATUS UNKNOWN" : "PROBING…"}</span>
        </Pill>
        <span className="font-mono text-xs text-white/40">{time.toLocaleTimeString([], { hour12: false })} UTC</span>
      </div>

      <button
        onClick={() => setCastOpen(true)} data-testid="cast-present-btn"
        className="flex h-9 shrink-0 items-center gap-2 rounded-xl border border-cyan-400/25 bg-cyan-400/10 px-2.5 text-cyan-200 hover:border-cyan-400/50 sm:px-3"
        title="Cast / Present to a TV"
      >
        <Tv className="h-4 w-4" />
        <span className="hidden text-xs font-medium sm:inline">Cast</span>
      </button>

      <button className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/60 hover:text-white" data-testid="notif-bell">
        <Bell className="h-4 w-4" />
        {notifCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-pink-500 px-1 text-[9px] font-bold text-white">{notifCount}</span>}
      </button>
      <ConflictAlert />
      <button className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/60 hover:text-white sm:flex" data-testid="topbar-settings">
        <Settings className="h-4 w-4" />
      </button>

      <CastModal open={castOpen} onClose={() => setCastOpen(false)} />
    </header>
  );
}

/* ==================== AI INTELLIGENCE PANEL ==================== */
export function AIPanel({ insights, loading }) {
  const sevTone = { high: "magenta", rising: "magenta", medium: "amber", watch: "amber", falling: "emerald", flat: "cyan" };
  return (
    <aside className="hidden w-[320px] shrink-0 flex-col border-l border-white/[0.06] bg-black/20 xl:flex">
      <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-cyan-300" />
          <span className="font-display text-sm font-semibold">AI Intelligence</span>
        </div>
        {insights?.risks_source ? <Pill tone="cyan">LIVE</Pill> : <Pill tone="amber">NO LIVE DATA</Pill>}
      </div>

      <div className="nx-scroll flex-1 space-y-6 overflow-y-auto p-5">
        {loading || !insights ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-white/[0.04]" />)}
          </div>
        ) : !insights.risks_source ? (
          <p className="text-xs leading-relaxed text-white/50">No live data source is connected, so no recommendations, risks or decisions are shown.</p>
        ) : (
          <>
            <Section title="Recommendations" icon={Route}>
              {insights.recommendations.map((r, i) => (
                <FeedItem key={i} delay={i} title={r.title} sub={r.impact} right={<Pill tone={sevTone[r.severity] || "cyan"}>{r.confidence}%</Pill>} />
              ))}
            </Section>
            <Section title="Detected Risks" icon={TriangleAlert}>
              {insights.risks.map((r, i) => (
                <FeedItem key={i} delay={i} title={r.title} sub={r.program} right={<Pill tone={sevTone[r.trend] || "amber"}>{r.level}</Pill>} />
              ))}
            </Section>
            <Section title="Opportunities" icon={Sparkles}>
              {insights.opportunities.map((o, i) => (
                <FeedItem key={i} delay={i} title={o.title} sub={o.value} />
              ))}
            </Section>
            <Section title="Suggested Decisions" icon={GitBranch}>
              {insights.decisions.map((d, i) => (
                <FeedItem key={i} delay={i} title={d.title} sub={`Due: ${d.due}`} right={<Pill tone="cyan">{d.confidence}%</Pill>} />
              ))}
            </Section>
            <Section title="Executive Talking Points" icon={Presentation}>
              <ul className="space-y-2">
                {insights.talkingPoints.map((t, i) => (
                  <li key={i} className="flex gap-2 text-xs text-white/60"><span className="text-cyan-400">▸</span>{t}</li>
                ))}
              </ul>
            </Section>
          </>
        )}
      </div>
    </aside>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-cyan-400/70" />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">{title}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function FeedItem({ title, sub, right, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: delay * 0.06 }}
      className="group flex items-start gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 transition-colors hover:border-cyan-400/25"
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium leading-snug text-white/85">{title}</div>
        {sub && <div className="mt-0.5 truncate text-[11px] text-white/40">{sub}</div>}
      </div>
      {right}
    </motion.div>
  );
}

/* ==================== COMMAND PALETTE ==================== */
export function CommandPalette({ open, setOpen, setView, runCommand }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 60); else setQ(""); }, [open]);

  const nav = NAV.filter((n) => n.label.toLowerCase().includes(q.toLowerCase()));
  const cmds = COMMANDS.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-start justify-center bg-black/60 p-4 pt-[12vh] backdrop-blur-md"
          onClick={() => setOpen(false)} data-testid="command-palette"
        >
          <motion.div
            initial={{ scale: 0.96, y: -10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: -10 }}
            onClick={(e) => e.stopPropagation()}
            className="nx-panel w-full max-w-xl overflow-hidden"
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
              <Command className="h-4 w-4 text-cyan-300" />
              <input
                ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search views or run an AI command…"
                className="w-full bg-transparent text-sm text-white placeholder:text-white/30 outline-none"
                data-testid="palette-input"
              />
              <kbd className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px] text-white/40">ESC</kbd>
            </div>
            <div className="nx-scroll max-h-[52vh] overflow-y-auto p-2">
              <Group label="Navigate">
                {nav.map((n) => (
                  <Row key={n.id} icon={ICONS[n.icon]} label={n.label} onClick={() => { setView(n.id); setOpen(false); }} testid={`palette-nav-${n.id}`} />
                ))}
              </Group>
              <Group label="AI Commands">
                {cmds.map((c) => (
                  <Row key={c.id} icon={ICONS[c.icon]} label={c.label} accent onClick={() => { runCommand(c.msg); setOpen(false); }} testid={`palette-cmd-${c.id}`} />
                ))}
              </Group>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Group({ label, children }) {
  if (!children || (Array.isArray(children) && children.filter(Boolean).length === 0)) return null;
  return (
    <div className="mb-1">
      <div className="px-3 py-1.5 font-mono text-[10px] tracking-widest text-white/30">{label}</div>
      {children}
    </div>
  );
}
function Row({ icon: Icon, label, onClick, accent, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-cyan-400/10 hover:text-white">
      {Icon && <Icon className={`h-4 w-4 ${accent ? "text-cyan-300" : "text-white/40"}`} />}
      {label}
      <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
    </button>
  );
}

/* ==================== FLOATING AI ASSISTANT ==================== */
export function AIAssistant({ open, setOpen, messages, onSend, busy }) {
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, busy]);

  const submit = (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[85] flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-500 text-black shadow-[0_0_30px_rgba(34,211,238,0.5)]"
        data-testid="assistant-toggle"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="nx-panel nx-scanline fixed bottom-24 right-6 z-[85] flex h-[520px] w-[min(92vw,380px)] flex-col overflow-hidden"
            data-testid="assistant-panel"
          >
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-400/15"><Bot className="h-4 w-4 text-cyan-300" /></div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">NexSys Assistant</div>
                <div className="font-mono text-[9px] text-emerald-400">● ONLINE · MOCKED</div>
              </div>
            </div>

            <div ref={scrollRef} className="nx-scroll flex-1 space-y-3 overflow-y-auto p-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    m.role === "user" ? "bg-cyan-400/15 text-white" : "border border-white/10 bg-white/[0.03] text-white/80"
                  }`} dangerouslySetInnerHTML={{ __html: m.html || m.text }} />
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5">
                    <span className="inline-flex gap-1">
                      {[0, 1, 2].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full bg-cyan-300 nx-pulse" style={{ animationDelay: `${d * 0.2}s` }} />)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 p-3">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {COMMANDS.slice(0, 3).map((c) => (
                  <button key={c.id} onClick={() => onSend(c.msg)} className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-white/50 hover:border-cyan-400/30 hover:text-white">
                    {c.label}
                  </button>
                ))}
              </div>
              <form onSubmit={submit} className="flex items-center gap-2">
                <input
                  value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Ask about risk, budget, roadmap…"
                  className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/40"
                  data-testid="assistant-input"
                />
                <button type="submit" className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400 text-black" data-testid="assistant-send">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ==================== FLOATING NOTIFICATIONS ==================== */
export function Notifications({ items, dismiss }) {
  return (
    <div className="pointer-events-none fixed right-4 top-[4.75rem] z-[70] flex w-[calc(100vw-2rem)] max-w-[300px] flex-col gap-2 sm:right-6">
      <AnimatePresence>
        {items.map((n) => (
          <motion.div
            key={n.id}
            initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 60 }}
            className="nx-panel pointer-events-auto flex items-start gap-3 p-3"
            data-testid="notification"
          >
            <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full nx-pulse" style={{ background: n.color }} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-white/90">{n.title}</div>
              <div className="truncate text-[11px] text-white/45">{n.body}</div>
            </div>
            <button onClick={() => dismiss(n.id)} className="text-white/30 hover:text-white"><X className="h-3.5 w-3.5" /></button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
